/*
  SSPS Predictive Search Optimization
  - Debounced async queries (200ms)
  - AbortController request cancellation
  - Stale response protection
  - Minimum 2-character query guard
  - Recent searches persistence via localStorage
  - Graceful network error handling with retry
  - Mobile full-screen keyboard responsiveness
*/

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.recentSearchesContainer = this.querySelector("#predictive-search-recent");
    this.isOpen = false;
    this.abortController = null;
    this.searchTerm = "";

    this.setupEventListeners();
    this.renderRecentSearches();
  }

  setupEventListeners() {
    if (this.input) {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.input.addEventListener("keyup", this.onKeyup.bind(this));
      this.input.addEventListener("keydown", this.onKeydown.bind(this));
      this.input.addEventListener("input", this.onInputUpdate.bind(this));
    }
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.setupTrendingChips();
  }

  setupTrendingChips() {
    this.querySelectorAll(".ssps-trending-chip").forEach((chip) => {
      chip.addEventListener("click", (e) => {
        e.preventDefault();
        const term = chip.dataset.term;
        if (this.input) {
          this.input.value = term;
          this.onInputUpdate();
          this.input.focus();
          this.onChange();
        }
      });
    });
  }

  onInputUpdate() {
    const clearBtn = this.querySelector("#Search-Modal-Reset");
    if (clearBtn && this.input) {
      if (this.input.value.trim().length > 0) {
        clearBtn.classList.remove("hidden");
      } else {
        clearBtn.classList.add("hidden");
      }
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim().replace(/\s+/g, " ") : "";
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    this.onInputUpdate();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.searchTerm = newSearchTerm;
    const discoveryEl = this.querySelector("#predictive-search-discovery");

    // Minimum search length rule (Req 14)
    if (this.searchTerm.length < 2) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.close(false);
      if (discoveryEl) discoveryEl.style.display = "block";
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = "none";
      this.renderRecentSearches();
      return;
    }

    // Hide discovery state when live predictive search is running
    if (discoveryEl) {
      discoveryEl.style.display = "none";
    }
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = "block";
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length) {
      event.preventDefault();
      return;
    }

    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
      return;
    }

    this.saveRecentSearch(query);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.closeResults(true);
      const discoveryEl = this.querySelector("#predictive-search-discovery");
      if (discoveryEl) discoveryEl.style.display = "block";
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = "none";
      this.onInputUpdate();
      this.renderRecentSearches();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.onInputUpdate();
    const discoveryEl = this.querySelector("#predictive-search-discovery");

    if (currentSearchTerm.length < 2) {
      if (discoveryEl) discoveryEl.style.display = "block";
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = "none";
      this.renderRecentSearches();
      return;
    }

    if (discoveryEl) discoveryEl.style.display = "none";
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = "block";

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        // Keep results visible on mobile drawer, close on desktop if focused outside
      }
    }, 100);
  }

  onKeyup(event) {
    const query = this.getQuery();
    if (query.length < 2) {
      this.close(false);
      this.renderRecentSearches();
      return;
    }

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault();
        this.switchOption("up");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li.predictive-search__list-item, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = allVisibleElements.indexOf(selectedElement);

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute("aria-selected", "true");
    if (selectedElement && selectedElement !== activeElement) {
      selectedElement.setAttribute("aria-selected", "false");
    }

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", activeElement.id || "");
    }
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
    if (selectedOption) {
      this.saveRecentSearch(this.getQuery());
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    const normalizedTerm = searchTerm.trim().replace(/\s+/g, " ");
    if (normalizedTerm.length < 2) return;

    // Abort pending in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const queryKey = normalizedTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    // Cache hit
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit || 6;
    const query = encodeURIComponent(normalizedTerm);
    const searchUrl = `${routes.predictive_search_url}?q=${query}&resources[type]=product,collection&resources[limit]=${limit}&options[prefix]=last&options[unavailable_products]=hide&section_id=predictive-search`;

    fetch(searchUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Search request failed (${response.status})`);
        }
        return response.text();
      })
      .then((text) => {
        // STALE RESPONSE PROTECTION: Discard response if query changed in the meantime
        if (this.getQuery().toLowerCase() !== queryKey) {
          return;
        }

        const parsed = new DOMParser().parseFromString(text, "text/html");
        const section = parsed.querySelector("#shopify-section-predictive-search");
        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === "AbortError" || error?.code === 20) {
          return;
        }
        this.renderErrorState(normalizedTerm);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.setAttribute("loading", "true");
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countEl = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countEl && countEl.textContent && this.statusElement) {
      this.statusElement.textContent = countEl.textContent;
    }
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", "true");
    this.setLiveRegionResults();
    this.open();

    // Intercept result clicks to save recent search and unlock body overflow
    this.predictiveSearchResults.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => {
        this.saveRecentSearch(this.getQuery());
        document.body.classList.remove("added__overlay_search");
      });
    });
  }

  renderErrorState(searchTerm) {
    if (!this.predictiveSearchResults) return;
    this.removeAttribute("loading");
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__error ssps-search-error">
        <p class="ssps-error-title">Search couldn't load. Please try again.</p>
        <button type="button" class="button button--secondary button--small ssps-retry-btn" id="PredictiveSearchRetry">
          Retry
        </button>
      </div>
    `;
    this.setAttribute("results", "true");
    this.open();

    const retryBtn = this.predictiveSearchResults.querySelector("#PredictiveSearchRetry");
    if (retryBtn) {
      retryBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.getSearchResults(searchTerm);
      });
    }
  }

  open() {
    this.setAttribute("open", "true");
    if (this.input) {
      this.input.setAttribute("aria-expanded", "true");
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", "false");

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", "false");
    }

    this.removeAttribute("loading");
    this.removeAttribute("open");

    const discoveryEl = this.querySelector("#predictive-search-discovery");
    if (discoveryEl) discoveryEl.style.display = "block";
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = "none";
    if (typeof this.onInputUpdate === "function") this.onInputUpdate();
  }

  // ==========================================
  // Recent Searches & Quick Category Discovery
  // ==========================================
  saveRecentSearch(term) {
    if (!term || typeof term !== "string") return;
    const clean = term.trim().replace(/\s+/g, " ");
    if (clean.length < 2) return;

    try {
      let recents = JSON.parse(localStorage.getItem("ssps_recent_searches") || "[]");
      recents = recents.filter((item) => item.toLowerCase() !== clean.toLowerCase());
      recents.unshift(clean);
      if (recents.length > 5) recents = recents.slice(0, 5);
      localStorage.setItem("ssps_recent_searches", JSON.stringify(recents));
    } catch (e) {}
  }

  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem("ssps_recent_searches") || "[]");
    } catch (e) {
      return [];
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem("ssps_recent_searches");
    } catch (e) {}
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentSearchesContainer) return;
    const recents = this.getRecentSearches();

    if (recents.length === 0) {
      this.recentSearchesContainer.innerHTML = "";
      this.recentSearchesContainer.style.display = "none";
      return;
    }

    this.recentSearchesContainer.style.display = "block";
    let markup = `
      <div class="ssps-search-section ssps-recent-section">
        <div class="ssps-section-header">
          <span class="ssps-section-title">
            <svg class="ssps-section-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Recent Searches
          </span>
          <button type="button" class="ssps-recent-clear-btn" id="SSPSClearRecent">Clear</button>
        </div>
        <div class="ssps-recent-tags">
          ${recents
            .map(
              (item) => `
            <button type="button" class="ssps-recent-chip" data-search-term="${item.replace(/"/g, '&quot;')}">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              <span>${item}</span>
            </button>
          `
            )
            .join("")}
        </div>
      </div>
    `;

    this.recentSearchesContainer.innerHTML = markup;

    // Attach chip click handlers
    this.recentSearchesContainer.querySelectorAll(".ssps-recent-chip").forEach((chip) => {
      chip.addEventListener("click", (e) => {
        e.preventDefault();
        const term = chip.dataset.searchTerm;
        if (this.input) {
          this.input.value = term;
          if (typeof this.onInputUpdate === "function") this.onInputUpdate();
          this.input.focus();
          this.onChange();
        }
      });
    });

    const clearBtn = this.recentSearchesContainer.querySelector("#SSPSClearRecent");
    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.clearRecentSearches();
      });
    }
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
